/**
 * LeadReport.jsx
 *
 * Shows lead‑generation metrics in a stacked‑bar / line combo chart.
 * Tooltip now hides any series whose value is 0 (or null / undefined).
 * Adds a download section allowing users to download lead CSVs for each campaign.
 */
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Flex,
  Text,
  Spinner,
  Select,
  Button,
  useTheme,
  useColorMode,
} from "@chakra-ui/react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faChartColumn,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
} from "recharts";
import { cqAPI } from "../../lib/Auth";

/**
 * Helper to fetch a blob URL for a file and return it for download.
 * @param {string} attachmentUrl
 * @returns {Promise<string|null>} blobUrl | null
 */
const fetchBlobUrl = async (attachmentUrl) => {
  try {
    const downloadUrl = await cqAPI.downloadFile(attachmentUrl);
    if (!downloadUrl) {
      console.error("File unavailable for download:", attachmentUrl);
      return null;
    }

    const response = await fetch(downloadUrl);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    return blobUrl;
  } catch (error) {
    console.error("Failed to fetch file:", error);
    return null;
  }
};

/** ------------------------------------------------------------------- */
/** Quick‑select date ranges                                            */
/** ------------------------------------------------------------------- */
const quickRanges = [
  "Today",
  "Yesterday",
  "Last 7 days",
  "Last 4 weeks",
  "Month to Date",
  "Quarter to date",
  "Year to date",
  "Custom",
];

const metricNames = ["Leads", "New Patients", "No shows"];

function LeadReport() {
  /** ----------------------------------------------------------------- */
  /** State & theme                                                     */
  /** ----------------------------------------------------------------- */
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRange, setSelectedRange] = useState("Today");
  const [dateRange, setDateRange] = useState([
    moment().startOf("day").toDate(),
    moment().endOf("day").toDate(),
  ]);
  const [patientType, setPatientType] = useState("t"); // "t" = Total (default), "p" = Planned
  const [chartData, setChartData] = useState([]);
  const [campaigns, setCampaigns] = useState([]); // [{ id, name }]

  const [startDate, endDate] = dateRange;

  const theme = useTheme();
  const { colorMode } = useColorMode();

  /** ----------------------------------------------------------------- */
  /** Palette helpers                                                    */
  /** ----------------------------------------------------------------- */
  const chartColors = useMemo(
    () =>
      Object.values(
        theme.colors.chartColors ?? {
          0: "#4e79a7",
          1: "#59a14f",
          2: "#9c755f",
          3: "#f28e2c",
          4: "#e15759",
          5: "#b07aa1",
        }
      ),
    [theme.colors.chartColors]
  );

  const metricBaseColor = useMemo(() => {
    return {
      Leads: chartColors[0] || "#4e79a7",
      "New Patients": chartColors[1] || "#59a14f",
      "No shows": chartColors[2] || "#9c755f",
    };
  }, [chartColors]);

  /* ------------------------------------------------------------------ */
  /* Helpers to derive unique colours within a stack                    */
  /* ------------------------------------------------------------------ */
  const hexToHsl = (hex) => {
    let r = 0,
      g = 0,
      b = 0;
    if (hex.length === 4) {
      r = parseInt(hex[1] + hex[1], 16);
      g = parseInt(hex[2] + hex[2], 16);
      b = parseInt(hex[3] + hex[3], 16);
    } else if (hex.length === 7) {
      r = parseInt(hex.slice(1, 3), 16);
      g = parseInt(hex.slice(3, 5), 16);
      b = parseInt(hex.slice(5, 7), 16);
    }
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h,
      s,
      l = (max + min) / 2;
    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }
      h *= 60;
    }
    return { h, s, l };
  };

  const hslToHex = (h, s, l) => {
    h /= 360;
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const r = hue2rgb(p, q, h + 1 / 3);
    const g = hue2rgb(p, q, h);
    const b = hue2rgb(p, q, h - 1 / 3);
    const toHex = (v) => Math.round(v * 255).toString(16).padStart(2, "0");
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  const shadeWithinRange = (baseHex, idx, total) => {
    const { h, s } = hexToHsl(baseHex);
    const minL = 0.25;
    const maxL = 0.75;
    const step = total === 1 ? 0.5 : idx / (total - 1);
    const newL = minL + step * (maxL - minL);
    return hslToHex(h, s, newL);
  };

  const colorFor = (metric, campaignIdx) => {
    const base = metricBaseColor[metric] || "#8884d8";
    return shadeWithinRange(base, campaignIdx, campaigns.length);
  };

  /** ----------------------------------------------------------------- */
  /** Utils                                                             */
  /** ----------------------------------------------------------------- */
  const formatDate = (d) => moment(d).format("L");

  const updateUrlHash = () => {
    const params = new URLSearchParams({
      startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
      endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
      selectedRange,
      patientType,
    }).toString();
    window.location.hash = params;
  };

  const handleQuickRangeChange = (value) => {
    setSelectedRange(value);
    let start, end;
    switch (value) {
      case "Today":
        start = moment().startOf("day");
        end = moment().endOf("day");
        break;
      case "Yesterday":
        start = moment().subtract(1, "days").startOf("day");
        end = moment().subtract(1, "days").endOf("day");
        break;
      case "Last 7 days":
        start = moment().subtract(6, "days").startOf("day");
        end = moment().endOf("day");
        break;
      case "Last 4 weeks":
        start = moment().subtract(4, "weeks").startOf("day");
        end = moment().endOf("day");
        break;
      case "Month to Date":
        start = moment().startOf("month");
        end = moment().endOf("day");
        break;
      case "Quarter to date":
        start = moment().startOf("quarter");
        end = moment().endOf("day");
        break;
      case "Year to date":
        start = moment().startOf("year");
        end = moment().endOf("day");
        break;
      case "Custom":
      default:
        start = null;
        end = null;
    }
    setDateRange([start ? start.toDate() : null, end ? end.toDate() : null]);
  };

  const renderCustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => (
    <Flex direction="column" width="100%" px={4}>
      <Flex justifyContent="center" alignItems="center" mt={4}>
        <Box
          p={2}
          borderRadius="md"
          borderWidth="2px"
          borderColor="purple.500"
          mr={2}
        >
          {startDate ? startDate.toLocaleDateString() : "Start Date"}
        </Box>
        <Text mx={2}>→</Text>
        <Box
          p={2}
          borderRadius="md"
          borderWidth="2px"
          borderColor="purple.500"
        >
          {endDate ? endDate.toLocaleDateString() : "End Date"}
        </Box>
      </Flex>
      <Flex justifyContent="space-between" alignItems="center" pt={2}>
        <Box as="button" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </Box>
        <Box>
          {date.toLocaleString("default", { month: "long", year: "numeric" })}
        </Box>
        <Box as="button" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
          <FontAwesomeIcon icon={faChevronRight} />
        </Box>
      </Flex>
    </Flex>
  );

  /** ----------------------------------------------------------------- */
  /** Fetch leads & build chart data                                    */
  /** ----------------------------------------------------------------- */
  const fetchLeads = async () => {
    setIsLoading(true);
    try {
      const formattedStart = formatDate(startDate);
      const formattedEnd = formatDate(endDate);
      const response = await cqAPI.get(
        `/reports/leads?rangeBeg=${formattedStart}&rangeEnd=${formattedEnd}&patient_type=${patientType}`
      );
      const body = response;
      const typeKey = patientType === "t" ? "total" : "planned";

      const nestedCats = body.data.categories[typeKey] || [];
      const categories = Array.from(new Set(nestedCats.flat())).sort(
        (a, b) => moment(a, "MMM/YYYY").valueOf() - moment(b, "MMM/YYYY").valueOf()
      );

      const allSeries = body.data.series[typeKey] ?? [];
      const campaignSeries = allSeries;
      setCampaigns(
        campaignSeries.map((s) => ({ id: s.campaign_id, name: s.chart_name }))
      );

      const valAt = (arr, idx) => (
        Array.isArray(arr) && arr[idx] !== null && arr[idx] !== undefined ? arr[idx] : 0
      );

      const mapped = categories.map((label, idx) => {
        const row = { category: label };
        campaignSeries.forEach((series, cIdx) => {
          const catArr = nestedCats[cIdx] || [];
          const pos = catArr.indexOf(label);
          metricNames.forEach((metric) => {
            const metricData = series.data.find((m) => m.name === metric);
            row[`${metric}_${series.campaign_id ?? series.chart_name}`] =
              pos !== -1 ? metricData?.data?.[pos] ?? 0 : 0;
          });
          const revenueData = series.data.find((m) => m.name === "Revenue");
          row[`Revenue_${series.campaign_id}`] = valAt(revenueData?.data, idx);
        });
        return row;
      });

      setChartData(mapped);
    } catch (err) {
      console.error("Error fetching leads", err);
    } finally {
      setIsLoading(false);
      updateUrlHash();
    }
  };

  /** ----------------------------------------------------------------- */
  /** Init on mount                                                     */
  /** ----------------------------------------------------------------- */
  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.slice(1));
    const sD = params.get("startDate") ? moment(params.get("startDate")).toDate() : moment().startOf("day").toDate();
    const eD = params.get("endDate") ? moment(params.get("endDate")).toDate() : moment().endOf("day").toDate();
    const pType = params.get("patientType") || "t";
    const sRange = params.get("selectedRange") || "Today";

    setDateRange([sD, eD]);
    setPatientType(pType);
    setSelectedRange(sRange);
    fetchLeads();
  }, []);

  /** ----------------------------------------------------------------- */
  /** Tooltip that hides zero‑value entries                             */
  /** ----------------------------------------------------------------- */
  const formatCurrency = (v) => (typeof v === "number" ? `$${v.toLocaleString()}` : v);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;

    // keep only values that are non‑zero / defined
    const filtered = payload.filter((p) => p.value !== 0 && p.value !== null && p.value !== undefined);
    if (filtered.length === 0) return null;

    const bg = colorMode === "light" ? "white" : "gray.700";

    return (
      <Box bg={bg} p={3} borderRadius="md" boxShadow="md">
        <Text fontWeight="bold" mb={1}>
          {label}
        </Text>
        {filtered.map((entry, idx) => (
          <Flex key={idx} justify="space-between">
            <Text mr={2}>{entry.name}</Text>
            <Text>
              {entry.name.startsWith("Revenue")
                ? formatCurrency(entry.value)
                : typeof entry.value === "number"
                ? entry.value.toLocaleString()
                : entry.value}
            </Text>
          </Flex>
        ))}
      </Box>
    );
  };

  /** ----------------------------------------------------------------- */
  /** Download helpers                                                  */
  /** ----------------------------------------------------------------- */
  const handleDownload = async (campaign) => {
    const campaignId = campaign.id
    const formattedStart = formatDate(startDate);
    const formattedEnd = formatDate(endDate);
    const attachmentUrl = `/reports/leads/download?rangeBeg=${formattedStart}&rangeEnd=${formattedEnd}&cid=${campaignId}`;

    const blobUrl = await fetchBlobUrl(attachmentUrl);
    if (!blobUrl) return;

    // Trigger client-side download
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = `leads_${campaign.name}_${formattedStart}_${formattedEnd}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  };

  /** ----------------------------------------------------------------- */
  /** Component render                                                  */
  /** ----------------------------------------------------------------- */
  const hoverBg = colorMode === "light" ? theme.colors.gray[50] : theme.colors.gray[700];

  const renderBars = () =>
    metricNames.flatMap((metric) =>
      campaigns.map((c, idx) => (
        <Bar
          key={`${metric}_${c.id}`}
          dataKey={`${metric}_${c.id}`}
          stackId={metric}
          barSize={20}
          fill={colorFor(metric, idx)}
          name={`${metric} – ${c.name}`}
          yAxisId="left"
        />
      ))
    );

  const renderRevenueLines = () =>
    campaigns.map((c, idx) => (
      <Line
        key={`Revenue_${c.id}`}
        yAxisId="right"
        type="monotone"
        dataKey={`Revenue_${c.id}`}
        strokeWidth={2}
        stroke={colorFor("Leads", idx)}
        dot={false}
        name={`Revenue – ${c.name}`}
      />
    ));

  return (
    <Box p={4} overflowY="auto" maxHeight="100vh" w="100%">
      {/* ------------------------- Filters --------------------------- */}
      <Flex justifyContent="flex-start" mb={4} wrap="wrap">
        <Flex align="center" mr={2} mb={2}>
          <Select value={selectedRange} onChange={(e) => handleQuickRangeChange(e.target.value)}>
            {quickRanges.map((range) => (
              <option key={range} value={range}>
                {range}
              </option>
            ))}
          </Select>
        </Flex>

        <Flex align="center" mr={2} mb={2}>
          <DatePicker
            className={`custom-datepicker ${colorMode === "light" ? "light-theme" : "dark-theme"}`}
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={(update) => {
              setDateRange(update);
              setSelectedRange("Custom");
            }}
            isClearable
            renderCustomHeader={renderCustomHeader}
          />
        </Flex>

        <Flex align="center" mr={2} mb={2} display="none">
          <Select value={patientType} onChange={(e) => setPatientType(e.target.value)}>
            <option value="t">Total</option>
            <option value="p">Planned</option>
          </Select>
        </Flex>

        <Flex ml="auto" mb={2}>
          <Button leftIcon={<FontAwesomeIcon icon={faChartColumn} />} onClick={fetchLeads}>
            Show Leads
          </Button>
        </Flex>
      </Flex>

      {!isLoading && <Flex data-report-download-target justify="flex-end" mb={2} className="no-print" />}

      {/* ------------------------- Chart / Empty / Spinner ----------- */}
      {isLoading ? (
        <Flex justifyContent="center" alignItems="center" height="60vh" w="100%">
          <Spinner color="blue.500" size="xl" />
        </Flex>
      ) : (
        <Box w="100%" h="70vh" borderWidth="1px" borderRadius="lg" p={4} _hover={{ bg: hoverBg }}>
          {chartData.length === 0 ? (
            <Flex w="100%" h="100%" justifyContent="center" alignItems="center">
              <Text>No data available for selected period.</Text>
            </Flex>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 20, right: 60, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="category" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `$${v.toLocaleString()}`} />

                {/* ---------------- Custom Tooltip -------------------- */}
                <RTooltip content={<CustomTooltip />} />

                <Legend
                  layout="vertical"
                  align="right"
                  verticalAlign="middle"
                  wrapperStyle={{ maxHeight: "100%", overflowY: "auto", right: 0, transform: "translateX(16px)" }}
                />

                {/* ---------------------- Generated Bars -------------- */}
                {renderBars()}

                {/* ---------------------- Revenue Lines per Campaign ---- */}
                {renderRevenueLines()}

                {/* ---------------------- Conversion Line (aggregate) -- */}
                <Line yAxisId="right" type="monotone" dataKey="Conversion" strokeWidth={2} stroke={metricBaseColor["No shows"]} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </Box>
      )}

      {/* ------------------------- Download Section ------------------- */}
      {campaigns.length > 0 && (
        <Box mt={6}>
          <Text fontWeight="bold" mb={2}>
            Download Leads
          </Text>
          <Flex wrap="wrap">
            {campaigns.map((c) => (
              <Button
                key={c.id}
                leftIcon={<FontAwesomeIcon icon={faDownload} />}
                mr={2}
                mb={2}
                onClick={() => handleDownload(c)}
              >
                {c.name}
              </Button>
            ))}
          </Flex>
        </Box>
      )}
    </Box>
  );
}

export default LeadReport;
